/**
 * The catalog behind every `{{` list, as a flat searchable array, plus the trigger
 * that drives the inline autocomplete. Pure — the picker button and the field's
 * autocomplete both read from here so they can never offer different things.
 *
 * Two kinds of row share the list, because from the writer's point of view they answer
 * the same question — *"what can I put in here that is not typed-out words?"*:
 *
 * - a **variable**, resolved per recipient at send time (`{{ customer.firstname }}`)
 * - a **saved text**, resolved per language from the partner's translation catalog
 *   (`{{ 'emails.greeting'|trans }}`)
 *
 * They differ only in the Twig a pick inserts, which is why each option carries its
 * own `snippet` rather than leaving hosts to rebuild it from the parts.
 */
import { buildVariableSnippet } from './EmailBodyEditor/insertion';
import {
  humanizeTransKey,
  humanizeVariablePath,
} from './EmailBodyEditor/labels';
import {
  bindingExpression,
  keyParts,
} from './EmailBodyEditor/translationBinding';
import { EmailVariableGroup } from './EmailBodyEditor/types';

export type VariableOptionKind = 'variable' | 'translation';

export interface VariableOption {
  kind: VariableOptionKind;
  namespace: string;
  name: string;
  /** `customer.firstname` / `emails.greeting` — the identifier, not the syntax. */
  path: string;
  /** "First name" — the primary line, for people who don't read dot-paths. */
  label: string;
  /** The exact Twig a pick inserts. */
  snippet: string;
  /** A saved text's current wording, when it has one for the language on screen. */
  text?: string;
  /**
   * Rows are grouped by this, and headers show `groupLabel`. Keyed by kind as well
   * as by name so a translation group that happens to be called `customer` never
   * lands under the variable namespace of the same name.
   */
  groupKey: string;
  groupLabel: string;
  /** Everything the search matches on, lowercased once at build time. */
  haystack: string;
}

export function buildVariableOptions(
  groups: EmailVariableGroup[] = [],
): VariableOption[] {
  return groups.flatMap((group) =>
    group.items.map((name) => {
      const path = `${group.namespace}.${name}`;
      const { label } = humanizeVariablePath(path);
      return {
        kind: 'variable' as const,
        namespace: group.namespace,
        name,
        path,
        label,
        snippet: buildVariableSnippet(group.namespace, name),
        groupKey: `variable:${group.namespace}`,
        groupLabel: group.namespace,
        haystack: `${path} ${label}`.toLowerCase(),
      };
    }),
  );
}

/** One row of the partner's translation catalog, as a picker needs it. */
export interface TranslationEntry {
  /** `group.key`, the form the `trans` filter looks up. */
  key: string;
  /** Its wording for the language on screen; absent when it has none. */
  text?: string;
}

/**
 * The partner's saved texts as list rows.
 *
 * **The wording is the primary line** when there is one. A key is a filing name —
 * `closing.content` and `closing.content_2` are indistinguishable until you can read
 * them — and what the writer is looking for is the sentence, not the identifier. The
 * identifier is on the line below, in the exact shape it will take in the template.
 *
 * Keys with no wording for this language are kept rather than dropped: a text that is
 * still to be translated is a text the template may legitimately reference, and the
 * humanized key stands in for it until it has words.
 */
export function buildTranslationOptions(
  entries: TranslationEntry[] = [],
): VariableOption[] {
  return entries.map(({ key, text }) => {
    const { group } = keyParts(key);
    const { label } = humanizeTransKey(key);
    const wording = text?.trim();
    return {
      kind: 'translation' as const,
      namespace: group,
      name: key.slice(group.length + 1) || key,
      path: key,
      label: wording || label,
      snippet: bindingExpression(key),
      text,
      groupKey: `translation:${group}`,
      // Says which of the two kinds this header covers without a legend, and in the
      // same raw-identifier register as the namespaces above it.
      groupLabel: `${group} · text`,
      haystack: `${key} ${label} ${text ?? ''}`.toLowerCase(),
    };
  });
}

/**
 * The word that turns the list into the saved-text list: `{{ trans`.
 *
 * The texts needed a way in of their own. A variable is found by typing what it is
 * called, but a text's *name* is a filing key nobody remembers — you know you want one
 * of your saved texts before you know which — so with nothing typed there was nothing
 * to type, and the texts sat below whatever variables the query happened to match.
 *
 * `trans` because that is the filter the pick writes, so anyone who has read a template
 * already knows the word. Any prefix of "translations" from four letters counts, which
 * means the list has settled by the time the word is finished and `{{ translation` works
 * as well as `{{ trans`. Four is the floor because `{{ tra` still belongs to
 * `booking.transport`.
 */
const TRANSLATION_KEYWORD = 'translations';
const KEYWORD_MIN_LENGTH = 4;

/** `trans`, `trans greet`, `trans.greet` — the keyword, and what narrows it. */
const KEYWORD_QUERY = /^([a-z]+)(?:[\s.]+(.*))?$/;

export function filterVariableOptions(
  options: VariableOption[],
  query: string,
): VariableOption[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return options;

  const keyword = KEYWORD_QUERY.exec(needle);
  if (
    keyword &&
    keyword[1].length >= KEYWORD_MIN_LENGTH &&
    TRANSLATION_KEYWORD.startsWith(keyword[1])
  ) {
    const texts = options.filter((option) => option.kind === 'translation');
    const rest = keyword[2]?.trim();
    /**
     * Only when there are texts to show. With an empty catalog the word is not a
     * keyword at all, and swallowing it would hide the `booking.transport` a partner
     * was halfway through typing.
     *
     * When there are, no match is the honest answer: the query asked for texts, so
     * falling back to the variables would answer a question nobody asked.
     */
    if (texts.length > 0) {
      return rest
        ? texts.filter((option) => option.haystack.includes(rest))
        : texts;
    }
  }

  return options.filter((option) => option.haystack.includes(needle));
}

export interface VariableTrigger {
  /** Offset of the `{{` that opened the list. */
  start: number;
  /** Offset just past the caret — `start`…`end` is what a pick replaces. */
  end: number;
  /** What has been typed after the braces. */
  query: string;
}

const OPENER = '{{';

/**
 * What may sit between `{{` and the caret while the list is still up: the
 * characters a variable path is made of, plus the space after the braces.
 * A `}`, a second `{` or a newline means this is no longer a variable being
 * typed — a finished `{{ customer.firstname }}` must not re-open the list when
 * the caret passes through it.
 */
const STILL_TYPING = /^[\w. ]*$/;

/**
 * The `{{ …` being typed at `caret`, or null when there is none.
 *
 * Deliberately not anchored to word boundaries: `{{` is unambiguous enough that
 * scanning back to the nearest one is right, and it means the list also opens
 * when the caret is put back inside an unfinished tag.
 */
export function findVariableTrigger(
  text: string,
  caret: number,
): VariableTrigger | null {
  const head = text.slice(0, Math.max(0, Math.min(caret, text.length)));
  const start = head.lastIndexOf(OPENER);
  if (start < 0) return null;

  const typed = head.slice(start + OPENER.length);
  if (!STILL_TYPING.test(typed)) return null;

  return { start, end: head.length, query: typed.trim() };
}
